import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { api, errMsg } from "@/lib/api";
import { ALIQUOTE, CANALI, calcIva, fmtEUR, parseNum, toInput, todayISO } from "@/lib/format";
import { Field, SimpleSelect, btnGhost, btnPrimary, inputCls } from "@/components/app/common";
import { CategoriaCombobox, catOpts } from "@/components/app/CategoriaCombobox";

const empty = { tipo: "costo", data: todayISO(), canale: "Generale", categoria: "", descrizione: "", imponibile: "", aliquota_iva: "22", reso: false };

const TipoToggle = ({ value, onChange }) => (
  <div className="grid grid-cols-2 gap-2 p-1 rounded-full bg-[#F4EFEA]" data-testid="movimento-tipo-toggle">
    {[["incasso", "Incasso"], ["costo", "Costo"]].map(([v, l]) => (
      <button type="button" key={v} data-testid={`movimento-tipo-${v}`} onClick={() => onChange(v)}
        className={`h-9 rounded-full text-sm font-medium transition-colors ${value === v ? (v === "incasso" ? "bg-[#8AA090] text-white" : "bg-[#D98A72] text-white") : "text-[#756B64] hover:text-[#2C2623]"}`}>
        {l}
      </button>
    ))}
  </div>
);

export function MovimentoDialog({ open, onOpenChange, movimento, categorie, onSaved }) {
  const [f, setF] = useState(empty);
  const [saving, setSaving] = useState(false);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (!open) return;
    setF(movimento
      ? { ...movimento, aliquota_iva: String(movimento.aliquota_iva), imponibile: toInput(Math.abs(movimento.imponibile)), reso: movimento.imponibile < 0 }
      : { ...empty, data: todayISO(), categoria: "" });
  }, [open, movimento]);

  const catOptions = useMemo(() => categorie.filter((c) => c.tipo === f.tipo).map((c) => ({ value: c.nome, label: c.nome })), [categorie, f.tipo]);
  const imp = parseNum(f.imponibile);
  const signed = imp == null ? 0 : f.reso ? -Math.abs(imp) : imp;
  const { iva, totale } = calcIva(signed, Number(f.aliquota_iva));

  const changeTipo = (tipo) => setF((p) => ({ ...p, tipo, categoria: tipo === "incasso" ? "Vendite" : "" }));

  const save = async () => {
    if (!f.categoria) return toast.error("Seleziona una categoria");
    if (imp == null) return toast.error("Inserisci un imponibile valido");
    setSaving(true);
    const body = { tipo: f.tipo, data: f.data, canale: f.canale, categoria: f.categoria, descrizione: f.descrizione, imponibile: signed, aliquota_iva: Number(f.aliquota_iva) };
    try {
      if (movimento) await api.put(`/movimenti/${movimento.id}`, body);
      else await api.post("/movimenti", body);
      toast.success(movimento ? "Movimento aggiornato" : "Movimento registrato con successo");
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl sm:max-w-lg max-h-[92vh] overflow-y-auto" data-testid="movimento-dialog" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-display font-medium text-2xl">{movimento ? "Modifica movimento" : "Nuovo movimento"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <TipoToggle value={f.tipo} onChange={changeTipo} />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Data"><Input data-testid="movimento-data-input" type="date" value={f.data} onChange={(e) => set("data")(e.target.value)} className={inputCls} /></Field>
            <Field label="Canale"><SimpleSelect testId="movimento-canale-select" value={f.canale} onChange={set("canale")} options={CANALI.map((c) => ({ value: c, label: c }))} /></Field>
          </div>
          <Field label="Categoria"><CategoriaCombobox testId="movimento-categoria-select" tipo={f.tipo} value={f.categoria} onChange={set("categoria")} placeholder="Scegli o crea categoria" options={catOptions} /></Field>
          <Field label="Descrizione"><Input data-testid="movimento-descrizione-input" value={f.descrizione} onChange={(e) => set("descrizione")(e.target.value)} className={inputCls} placeholder="Es. Fattura fornitore n. 123" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Imponibile (€)"><Input data-testid="movimento-imponibile-input" inputMode="decimal" value={f.imponibile} onChange={(e) => set("imponibile")(e.target.value)} className={inputCls} placeholder="0,00" /></Field>
            <Field label="Aliquota IVA"><SimpleSelect testId="movimento-aliquota-select" value={f.aliquota_iva} onChange={set("aliquota_iva")} options={ALIQUOTE.map((a) => ({ value: String(a), label: `${a}%` }))} /></Field>
          </div>
          <label className="flex items-center justify-between gap-3 rounded-xl border border-[#EAE3DC] px-4 py-3">
            <span className="text-sm"><b className="font-semibold">Reso / rimborso</b><span className="block text-xs text-[#756B64]">L'importo verrà registrato come negativo</span></span>
            <Switch data-testid="movimento-reso-switch" checked={f.reso} onCheckedChange={set("reso")} />
          </label>
          <div className="grid grid-cols-3 gap-2 rounded-xl bg-[#FAF7F5] p-4 text-center" data-testid="movimento-preview">
            <div><div className="text-[11px] text-[#756B64]">Imponibile</div><div className="font-semibold tabular" data-testid="preview-imponibile">{fmtEUR(signed)}</div></div>
            <div><div className="text-[11px] text-[#756B64]">IVA</div><div className="font-semibold tabular" data-testid="preview-iva">{fmtEUR(iva)}</div></div>
            <div><div className="text-[11px] text-[#756B64]">Totale</div><div className="font-semibold tabular text-[#8C6D62]" data-testid="preview-totale">{fmtEUR(totale)}</div></div>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" className={btnGhost} onClick={() => onOpenChange(false)} data-testid="movimento-cancel-button">Annulla</Button>
          <Button className={btnPrimary} onClick={save} disabled={saving} data-testid="movimento-save-button">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
